// src/pages/reserv/ReservList.jsx
import React, {useMemo, useState} from 'react';
import {format, isPast, isToday, isTomorrow, parseISO} from 'date-fns';
import {ru} from 'date-fns/locale';
import {useBookingsAll} from '../../hooks/useBookingsAll'; // новый хук — все брони
import {CheckCircle2, Edit2, Phone, Search, Trash2, Users} from 'lucide-react';
import {apiAdd, apiDelete, apiUpdate} from "../../api/reservations";
import EditReserv from "../../components/modal/editReserv/EditReserv";
import {useOutletContext} from 'react-router-dom'; // ← ДОБАВИЛ ЭТО!
import useScrollPosition from '../../hooks/useScrollPosition';
import ExportPDF from "../../components/ExportPDF"; // ← ИМПОРТИРУЕМ ХУК


const today = new Date();

export default function ReservList() {
    const {bookings, loading, refetch} = useBookingsAll();
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all'); // all, today, tomorrow, past, checked
    const [editModal, setEditModal] = useState({isOpen: false, item: null});
    const [deleteModal, setDeleteModal] = useState({isOpen: false, item: null});


    const context = useOutletContext();
    // console.log('📦 Контекст из Layout:', context);
    const {scrollContainerRef, scrollPositionRef, restoreScrollPosition} = context;

    const {savePosition, restorePosition} = useScrollPosition(scrollContainerRef);

    const filtered = useMemo(() => {
        let list = bookings.filter(b => {
            const nameMatch = b.name?.toLowerCase().includes(search.toLowerCase());
            const roomMatch = b.room?.toString().includes(search);
            return nameMatch || roomMatch;
        });

        switch (filter) {
            case 'today':
                return list.filter(b => b.checkOut === format(today, 'yyyy-MM-dd'));
            case 'tomorrow':
                return list.filter(b => isTomorrow(parseISO(b.checkOut)));
            case 'past':
                return list.filter(b => isPast(parseISO(b.checkOut)) && !b.checked_out);
            case 'checked':
                return list.filter(b => b.checked_out);
            default:
                return list;
        }
    }, [bookings, search, filter]);

    if (loading) return <div className="p-8 text-center">
        <div
            className="inline-block animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
    </div>;

    const handleDeleteClick = (item) => {
        savePosition(); // ← сохраняем перед открытием модалки удаления
        setDeleteModal({isOpen: true, item});
    };

    const handleDelete = async () => {
        try {
            await apiDelete(deleteModal.item.id);
            closeDeleteModal();
            await refetch();
            restorePosition();
        } catch (err) {
            alert('Ошибка удаления');
        }
    };


    const handleEdit = (item) => {
        setEditModal({
            isOpen: true,
            item: item || { // если null — создаём пустой объект
                id: null,
                Client_Name: '',
                phone: '',
                manager_id: '',
                rooms_id: '1',
                checkIn: format(new Date(), 'yyyy-MM-dd'),
                checkOut: format(new Date(), 'yyyy-MM-dd'),
            }
        });
        savePosition();
        setEditModal({isOpen: true, item});
    };

    const closeEditModal = () => setEditModal({isOpen: false, item: null});
    const closeDeleteModal = () => setDeleteModal({isOpen: false, item: null});


    return (
        <div className="pt-12 px-4 pb-32 min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            <div className="max-w-4xl mx-auto">
                <h1 className="text-4xl font-extrabold text-gray-900 mb-2 flex items-center gap-3">
                    <Users className="w-10 h-10 text-blue-600"/>
                    Все бронирования
                </h1>
                <p className="text-gray-600 mb-6">Всего: {filtered.length} активных</p>

                {/* Поиск + фильтры */}
                <div className="mb-8 space-y-4">
                    <div className="relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5"/>
                        <input
                            type="text"
                            placeholder="Поиск по имени или номеру..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="w-full pl-12 pr-4 py-4 rounded-2xl border border-gray-300 focus:border-blue-500 focus:outline-none transition"
                        />
                    </div>

                    <div className="flex flex-wrap gap-2">
                        {['all', 'today', 'tomorrow', 'past', 'checked'].map(f => (
                            <button
                                key={f}
                                onClick={() => setFilter(f)}
                                className={`px-5 py-2 rounded-full text-sm font-medium transition ${
                                    filter === f
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                                {f === 'all' && 'Все'}
                                {f === 'today' && 'Сегодня выезд'}
                                {f === 'tomorrow' && 'Завтра выезд'}
                                {f === 'past' && 'Просроченные'}
                                {f === 'checked' && 'Выезд отмечен'}
                            </button>

                        ))}
                        <ExportPDF bookings={filtered}/>
                    </div>
                </div>

                {/* Список */}
                {filtered.length === 0 ? (
                    <div className="text-center py-20">
                        <div className="text-8xl mb-6">Calendar</div>
                        <p className="text-2xl font-bold text-gray-600">Нет бронирований</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {filtered.map(b => (
                            <div
                                key={b.id}
                                className="bg-white rounded-2xl shadow-md border border-gray-200 p-5 hover:shadow-xl transition"
                            >
                                <div className="flex items-start gap-4 flex-1 min-w-0">
                                    <div
                                        className="w-14 h-14 bg-gradient-to-br from-blue-600 to-purple-600 text-white rounded-full flex items-center justify-center text-xl font-bold shrink-0">
                                        {b.room}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-xl font-bold text-gray-900 truncate">{b.name || 'Гость'}</h3>
                                        <p className="text-sm text-gray-600">
                                            Заезд: {format(parseISO(b.checkIn), 'd MMM', {locale: ru})} →
                                            Выезд: {format(parseISO(b.checkOut), 'd MMM', {locale: ru})}
                                        </p>
                                        <div className="mt-2">
                                            {b.checked_out ? (
                                                <span
                                                    className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded-full">
                                                    Выезд отмечен
                                                </span>
                                            ) : isPast(parseISO(b.checkOut)) ? (
                                                <span
                                                    className="text-xs font-medium text-red-600 bg-red-100 px-2 py-1 rounded-full">
                                                    Просрочен
                                                </span>
                                            ) : (
                                                <span className="text-xs text-gray-500">Ожидается выезд</span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Кнопки */}
                                <div className="flex justify-end gap-3 mt-5 pt-4 border-t border-gray-100">
                                    {b.phone && (
                                        <a href={`tel:${b.phone}`}
                                           className="p-3.5 bg-green-100 rounded-2xl text-green-600 hover:bg-green-200 transition shadow-sm">
                                            <Phone size={22} strokeWidth={2.5}/>
                                        </a>
                                    )}
                                    <button
                                        onClick={() => handleEdit(b)}
                                        className="p-3.5 bg-indigo-100 rounded-2xl text-indigo-600 hover:bg-indigo-200 transition shadow-sm"
                                    >
                                        <Edit2 size={22} strokeWidth={2.5}/>
                                    </button>
                                    <button
                                        onClick={() => handleDeleteClick(b)}
                                        className="p-3.5 bg-red-100 rounded-2xl text-red-600 hover:bg-red-200 transition shadow-sm"
                                    >
                                        <Trash2 size={22} strokeWidth={2.5}/>
                                    </button>
                                    {!b.checked_out && isToday(parseISO(b.checkOut)) && (
                                        <button
                                            className="p-3.5 bg-emerald-100 rounded-2xl text-emerald-600 hover:bg-emerald-200 transition shadow-sm">
                                            <CheckCircle2 size={22} strokeWidth={2.5}/>
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Модалка редактирования */}
                <EditReserv
                    isOpen={editModal.isOpen}
                    onClose={closeEditModal}
                    item={editModal.item}
                    onSave={async (form) => {
                        try {
                            console.log('form : ', form);

                            if (form.id)
                                await apiUpdate(form);
                            else
                                await apiAdd(form);
                            await refetch();
                            restoreScrollPosition();
                            closeEditModal();
                            return true;
                        } catch {
                            return false;
                        }
                    }}
                />

                {/* КНОПКА "ДОБАВИТЬ БРОНЬ" — плавающая справа снизу */}
                <button
                    onClick={() => handleEdit(null)} // null = новая бронь
                    className="fixed bottom-28 right-4 z-40 w-14 h-14 bg-gradient-to-br from-blue-600 to-indigo-600 text-white rounded-full shadow-2xl hover:shadow-indigo-500/50 transition-all hover:scale-110 flex items-center justify-center"
                >
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4"/>
                    </svg>
                </button>

                {/* Модалка удаления */}
                {deleteModal.isOpen && (
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" onClick={closeDeleteModal}>
                        <div
                            className="fixed inset-x-4 top-1/2 -translate-y-1/2 bg-white rounded-3xl p-6 shadow-2xl max-w-md mx-auto">
                            <h3 className="text-xl font-bold mb-4">Удалить бронь?</h3>
                            <p className="text-gray-600 mb-6">
                                {deleteModal.item?.name || 'Гость'} • Номер {deleteModal.item?.room}
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={closeDeleteModal}
                                    className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50"
                                >
                                    Отмена
                                </button>
                                <button
                                    onClick={handleDelete}
                                    className="flex-1 py-3 rounded-xl bg-red-600 text-white font-bold hover:bg-red-700"
                                >
                                    Удалить
                                </button>
                            </div>
                        </div>
                    </div>
                )}

            </div>


            {/* Скрытый блок для PDF */}
            <div id="pdf-content" style={{
                position: 'fixed',
                left: '-100vw',
                top: 0,
                pointerEvents: 'none',
                zIndex: -9999
            }}>
                <div className="p-8 bg-white"
                     style={{width: '300mm', minHeight: '297mm'}}> {/* Увеличил ширину в 1.5 раза */}
                    {/* Заголовок */}
                    <h1 className="text-4xl font-bold text-center mb-6 text-gray-900">HOTEL MANAGER</h1>
                    <p className="text-2xl text-center text-gray-600 mb-8">
                        Отчёт по бронированиям • {new Date().toLocaleDateString('ru')}
                    </p>

                    {/* Таблица */}
                    <table className="w-full border-collapse" style={{fontSize: '18px'}}>
                        <thead>
                        <tr className="border-b-2 border-gray-800">
                            <th className="text-left py-3 px-4 font-bold" style={{width: '15%'}}>Номер</th>
                            <th className="text-left py-3 px-4 font-bold" style={{width: '25%'}}>Гость</th>
                            <th className="text-left py-3 px-4 font-bold" style={{width: '15%'}}>Заезд</th>
                            <th className="text-left py-3 px-4 font-bold" style={{width: '15%'}}>Выезд</th>
                            <th className="text-left py-3 px-4 font-bold" style={{width: '20%'}}>Статус</th>
                            <th className="text-left py-3 px-4 font-bold" style={{width: '10%'}}>Телефон</th>
                        </tr>
                        </thead>
                        <tbody>
                        {filtered.map((b, index) => (
                            <tr key={b.id} className={`border-b ${index % 2 === 0 ? 'bg-gray-50' : ''}`}>
                                <td className="py-3 px-4 font-semibold">{b.room}</td>
                                <td className="py-3 px-4">{b.name}</td>
                                <td className="py-3 px-4">{format(parseISO(b.checkIn), 'dd.MM.yyyy')}</td>
                                {/* Полный формат даты */}
                                <td className="py-3 px-4">{format(parseISO(b.checkOut), 'dd.MM.yyyy')}</td>
                                {/* Полный формат даты */}
                                <td className="py-3 px-4">
                        <span className={`font-semibold ${
                            b.checked_out ? 'text-green-600' :
                                isPast(parseISO(b.checkOut)) ? 'text-red-600' : 'text-blue-600'
                        }`}>
                            {b.checked_out ? 'Выезд' : isPast(parseISO(b.checkOut)) ? 'Просрочен' : 'Активна'}
                        </span>
                                </td>
                                <td className="py-3 px-4 text-gray-600">{b.phone || '-'}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>

                    {/* Подвал с итогами */}
                    <div className="mt-8 pt-6 border-t-2 border-gray-300">
                        <p className="text-xl text-gray-700">
                            Всего бронирований: <span className="font-bold text-2xl">{filtered.length}</span>
                        </p>
                        <p className="text-lg text-gray-500 mt-2">
                            Сгенерировано: {new Date().toLocaleString('ru')}
                        </p>
                    </div>
                </div>
            </div>

            {/* Кнопка экспорта */}
            {/*<ExportPDF bookings={filtered}/>*/}


        </div>
    );
}
